import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {FadeInAnimation} from 'UI/animations/shared';

import {transient} from 'Application/configs/emotionTransientProps';
import {generateBgImage} from 'Application/utils/bgImages';
import {isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {WithChildren} from 'types/global';
import type {Media} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `Card` component.
 * This interface specifies the configuration options available for customizing the card's appearance and behavior, including background imagery and spacing controls.
 * These properties allow developers to create flexible card layouts that can adapt to various content types and design requirements.
 */
interface ComponentProps {
    /**
     * Optional background image media object that will be displayed behind the card content.
     * When provided, this image will be processed and applied as a CSS background image with appropriate styling and focal point positioning.
     * The image enhances the visual appeal of the card and can be used for branding or contextual imagery.
     */
    bgImage?: Media | string | null;
    /**
     * Additional CSS class name that can be applied to the card wrapper component for custom styling purposes.
     * This property allows developers to extend the card's styling beyond the default theme-based styles.
     * The className is passed through to the outermost wrapper element for maximum styling flexibility.
     */
    className?: string;
    /**
     * Controls whether the card should maintain its full span height regardless of content or reduce it by one row.
     * When enabled, the card occupies the exact number of rows specified in the span property without any automatic adjustments.
     * This property is useful for maintaining consistent card heights in complex grid layouts where precise positioning is required.
     *
     * @default false
     */
    constantSpan?: boolean;
    /**
     * Controls whether the card container should include internal padding spacing around its content.
     * When enabled, applies consistent spacing that follows the design system's grid spacing conventions.
     * This property helps maintain proper content spacing and visual hierarchy within the card layout.
     *
     * @default true
     */
    hasPadding?: boolean;
    /**
     * Defines the number of grid rows that the card component should span within its parent grid container.
     * This property controls the vertical space allocation for the card, allowing it to occupy multiple grid rows for larger content areas.
     * The span value directly affects the card's height and positioning within complex grid-based layouts.
     */
    span: number;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     * The identifier enables automated testing frameworks to locate and interact with the specific card component instance.
     *
     * @default 'Card'
     */
    testId?: string;
}

/**
 * The Card component renders a styled container with optional background image and padding capabilities.
 * This component serves as a flexible layout wrapper that can display content within a rounded card-like interface with customizable styling options.
 * It supports background images through the Media type and provides configurable padding for different layout requirements.
 *
 * @param props            The component props.
 * @param props.bgImage    Optional background image media object that will be applied to the card container.
 * @param props.children   The child elements to be rendered inside the card container.
 * @param props.hasPadding Controls whether the card should include internal padding spacing.
 * @param props.testId     Unique identifier for testing purposes to locate the component during automated tests.
 * @returns The rendered Card component as a JSX element.
 *
 * @example
 * ```tsx
 * <Card
 *     bgImage={mediaObject}
 *     hasPadding={true}
 *     testId="hero-card"
 * >
 *     <h2>Card Content</h2>
 *     <p>This is the card content.</p>
 * </Card>
 * ```
 */
const Card = observer(({
    bgImage,
    children,
    className,
    constantSpan = false,
    hasPadding = true,
    span,
    testId = 'Card'
}: WithChildren<ComponentProps>) => (
    <Wrapper $span={span} className={className} variants={FadeInAnimation}>
        <CardWrapper
            $bgImage={bgImage}
            $constantSpan={constantSpan}
            $hasPadding={hasPadding}
            $span={span}
            data-cy={testId}
        >
            {children}
        </CardWrapper>
    </Wrapper>
));

Card.displayName = 'Card';

export {Card};

interface WrapperProps {
    $span: number;
}

const Wrapper = styled(motion.div, transient)<WrapperProps>`
    display: grid;
    grid-row: span ${({$span}) => $span};
    grid-template-columns: 1fr;
    grid-template-rows: subgrid;
    min-width: 0;
    width: 100%;
`;

interface CardWrapperProps {
    $bgImage?: Media | string | null | undefined;
    $constantSpan?: boolean;
    $hasPadding: boolean;
    $span: number;
}

const CardWrapper = styled.div<CardWrapperProps>`
    background-color: ${({theme}) => theme.colors.backgroundLight};
    /* background-image: ${({$bgImage}) => (isDefinedAndPopulated($bgImage) ? `, ${generateBgImage($bgImage)}` : null)}; */
    background-position-x: ${({$bgImage}) => (isDefinedAndPopulated($bgImage) ? `${$bgImage.focalX}%` : 'center')};
    background-position-y: ${({$bgImage}) => (isDefinedAndPopulated($bgImage) ? `${$bgImage.focalY}%` : 'center')};
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 16px;
    color: ${({$bgImage, theme}) => (isDefinedAndPopulated($bgImage) ? theme.colors.textLight : null)};
    display: grid;
    grid-row: span ${({$constantSpan, $span}) => ($constantSpan ? $span : $span - 1)};
    grid-template-columns: 1fr;
    grid-template-rows: subgrid;
    padding: ${({$hasPadding}) => ($hasPadding ? spacing(7) : 0)};

    ${({$bgImage}) => (isDefinedAndPopulated($bgImage) && generateBgImage($bgImage, 'linear-gradient(to top, rgba(86, 86, 86, 1) 0%, rgba(255, 255, 255, 0) 60%)'))};
`;